import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import getStyled from "../../hooks/getStyled";
import SFButton from "../button";
import SFIconButton from "../button/iconButton";
import SFDropdown from "../dropdown";
import { SFStack } from "../grid";
import { SFMoreVerticalIcon } from "../icons";
import PropTypes from "prop-types";
import SFDialog from "../dialog";

const { styled } = getStyled();

const StyledContainer = styled("div")(() => ({
  position: "sticky",
  bottom: 0,
  backgroundColor: "white",
  height: "56px",
  width: "100%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  paddingLeft: "24px",
  paddingRight: "24px",
}));

const SFStackStyled = styled(SFStack)(() => ({
  flexDirection: "row",
  alignItems: "center",
  gap: "24px",
}));

const SFFooterActions = ({ actions = [], dropDownActions = [] }) => {
  const { t } = useTranslation();

  const [dropDownState, setDropDownState] = useState({
    open: false,
    anchorEl: null,
  });

  const [dialog, setDialog] = useState({
    open: false,
    header: "",
    message: "",
    onConfirm: () => {},
  });

  const toggleHandler = (e) => {
    e.stopPropagation();
    setDropDownState((prevState) => ({
      ...prevState,
      open: !prevState.open,
      anchorEl: e.currentTarget,
    }));
  };

  const closeHandler = () => {
    setDropDownState((prevState) => ({
      ...prevState,
      open: false,
    }));
  };

  const confirmActionHandler = (action) => {
    setDialog({
      open: true,
      header: action.confirmationHeader,
      message: action.confirmationMessage,
      onConfirm: () => {
        action.onClick();
        handleDialogClose();
      },
    });
  };

  const handleDialogClose = () => {
    setDialog((prevState) => ({ ...prevState, open: false }));
  };

  const visibleActions = actions
    .filter((action) => !action.hidden)
    .map((action) => {
      if (action.needsConfirmation) {
        return {
          ...action,
          onClick: confirmActionHandler.bind(this, action),
        };
      } else {
        return action;
      }
    });

  const visibleDropDownActions = dropDownActions
    .filter((action) => !action.hidden)
    .map((action) => {
      if (action.needsConfirmation) {
        return {
          ...action,
          onClick: confirmActionHandler.bind(this, action),
        };
      } else {
        return action;
      }
    });

  return (
    <>
      <SFDialog
        variant="default"
        title={dialog.header}
        message={dialog.message}
        open={dialog.open}
        onClose={handleDialogClose}
        primaryButton
        primaryButtonProps={{
          label: t("components.footerActionsModal.confirmButton"),
          onClick: dialog.onConfirm,
        }}
        secondaryButton
        secondaryButtonProps={{
          label: t("components.footerActionsModal.cancelButton"),
          onClick: handleDialogClose,
        }}
      />
      <StyledContainer>
        <SFStackStyled>
          {visibleDropDownActions && visibleDropDownActions.length > 0 && (
            <SFDropdown
              open={dropDownState.open}
              anchorEl={dropDownState.anchorEl}
              onClose={closeHandler}
              trigger={
                <SFIconButton onClick={toggleHandler} color="black">
                  <SFMoreVerticalIcon />
                </SFIconButton>
              }
              items={visibleDropDownActions}
              position={50}
              placement={"top"}
            />
          )}
          {visibleActions &&
            visibleActions.map((action, index) => (
              <SFButton onClick={action.onClick} key={index} icon={action.icon}>
                {action.label}
              </SFButton>
            ))}
        </SFStackStyled>
      </StyledContainer>
    </>
  );
};

export default SFFooterActions;

SFFooterActions.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      icon: PropTypes.element,
      onClick: PropTypes.func,
      needsConfirmation: PropTypes.bool,
      confirmationHeader: PropTypes.string,
      confirmationMessage: PropTypes.string,
    })
  ),
  dropDownActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      icon: PropTypes.element,
      onClick: PropTypes.func,
      hidden: PropTypes.bool,
      needsConfirmation: PropTypes.bool,
      confirmationHeader: PropTypes.string,
      confirmationMessage: PropTypes.string,
    })
  ),
};
